import { useState, useCallback, useRef } from 'react';
import Map from './components/Map.jsx';
import SearchBar from './components/SearchBar.jsx';
import ParcelPanel from './components/ParcelPanel.jsx';
import { fetchParcelByAddress, fetchParcelByPoint, fetchParcelsByPolygon, fetchParcelById } from './lib/regrid.js';
import { reverseLookup } from './lib/reverseLookup.js';
import { extractLegalDescriptionFromFile } from './lib/claudeApi.js';

export default function App() {
  const [parcels, setParcels] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [flyTo, setFlyTo] = useState(null);
  const [vertexCount, setVertexCount] = useState(0);
  const [finishSignal, setFinishSignal] = useState(0);

  // Phase 5 + 5-upload state
  const [reverseGeom, setReverseGeom] = useState(null);
  const [reverseText, setReverseText] = useState('');
  const [reverseStatus, setReverseStatus] = useState(null);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState(null);
  const [baseLayer, setBaseLayer] = useState('street');
  const fileInputRef = useRef(null);

  const handleSearch = useCallback(async (query) => {
    setLoading(true);
    setStatus({ kind: 'info', msg: 'Looking up parcel...' });
    try {
      const coordMatch = query.match(/^\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*$/);
      let result;
      if (coordMatch) {
        const lat = parseFloat(coordMatch[1]);
        const lon = parseFloat(coordMatch[2]);
        result = await fetchParcelByPoint(lat, lon);
        setFlyTo({ lng: lon, lat, zoom: 17 });
      } else if (looksLikeParcelId(query)) {
        // Try parcel ID lookup first, fall back to address if nothing matches
        try {
          result = await fetchParcelById(query);
        } catch (e) {
          result = { type: 'FeatureCollection', features: [] };
        }
        if (!result?.features?.length) {
          result = await fetchParcelByAddress(query);
        }
        const first = result?.features?.[0];
        if (first) {
          const [lng, lat] = centroid(first);
          setFlyTo({ lng, lat, zoom: 17 });
        }
      } else {
        result = await fetchParcelByAddress(query);
        const first = result?.features?.[0];
        if (first) {
          const [lng, lat] = centroid(first);
          setFlyTo({ lng, lat, zoom: 17 });
        }
      }
      const count = result?.features?.length || 0;
      if (count === 0) {
        setParcels(null);
        setStatus({ kind: 'error', msg: 'No parcels found for that query.' });
      } else {
        setParcels(result);
        setStatus({ kind: 'success', msg: `Found ${count} parcel${count > 1 ? 's' : ''}.` });
      }
    } catch (err) {
      console.error(err);
      setParcels(null);
      setStatus({ kind: 'error', msg: err.message || 'Lookup failed.' });
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePolygonDrawn = useCallback(async (polygon) => {
    setDrawMode(false);
    setLoading(true);
    setStatus({ kind: 'info', msg: 'Looking up parcels inside drawn polygon...' });
    try {
      const result = await fetchParcelsByPolygon(polygon);
      const count = result?.features?.length || 0;
      if (count === 0) {
        setParcels(null);
        setStatus({ kind: 'error', msg: 'No parcels inside that polygon.' });
      } else {
        setParcels(result);
        setStatus({ kind: 'success', msg: `Found ${count} parcel${count > 1 ? 's' : ''}.` });
      }
    } catch (err) {
      console.error(err);
      setStatus({ kind: 'error', msg: err.message || 'Lookup failed.' });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleReverseLookup = useCallback(async () => {
    const text = reverseText.trim();
    if (!text) return;
    setReverseLoading(true);
    setReverseStatus({ kind: 'info', msg: 'Parsing description with Claude...' });
    setReverseGeom(null);
    setParcels(null);
    try {
      const fc = await reverseLookup(text);
      const count = fc.features.length;
      setReverseGeom(fc);
      setReverseStatus({ kind: 'success', msg: `Matched ${count} PLSS cell${count > 1 ? 's' : ''}. Fetching Regrid parcels...` });
      if (count > 0) {
        const [lng, lat] = centroid(fc.features[0]);
        setFlyTo({ lng, lat, zoom: 13 });
        // Pick the polygon to center Regrid lookup on. Prefer the metes walk;
        // fall back to the aliquot container.
        const metesFeature = fc.features.find((f) => f?.properties?.kind === 'metes_walk');
        const queryPolygon = metesFeature || fc.features[0];
        const [cLng, cLat] = centroid(queryPolygon);
        const closureSuffix = metesFeature?.properties?.closureRatio != null
          ? ` Closure 1:${Number(metesFeature.properties.closureRatio).toLocaleString()} (${metesFeature.properties.closureQuality}).`
          : '';
        try {
          // Point query hits /api/v2/parcels/point which we\'ve confirmed works.
          // This returns the single parcel containing that point (if any).
          const regrid = await fetchParcelByPoint(cLat, cLng);
          const rcount = regrid?.features?.length || 0;
          if (rcount > 0) {
            setParcels(regrid);
            setReverseStatus({ kind: 'success', msg: `Matched ${count} PLSS cell${count > 1 ? 's' : ''}. Found ${rcount} Regrid parcel at the tract center.${closureSuffix}` });
          } else {
            setReverseStatus({ kind: 'success', msg: `Matched ${count} PLSS cell${count > 1 ? 's' : ''}. No Regrid parcel at the tract center.${closureSuffix}` });
          }
        } catch (regridErr) {
          console.warn('Regrid point query failed after reverse lookup:', regridErr);
          setReverseStatus({ kind: 'success', msg: `Matched ${count} PLSS cell${count > 1 ? 's' : ''}.${closureSuffix} Regrid overlay failed: ${regridErr.message || 'unknown'}` });
        }
      }
    } catch (err) {
      setReverseStatus({ kind: 'error', msg: err.message || 'Reverse lookup failed.' });
    } finally {
      setReverseLoading(false);
    }
  }, [reverseText]);

  const clearReverse = () => {
    setReverseGeom(null);
    setReverseStatus(null);
    setReverseText('');
    setUploadedName(null);
  };

  const handleFileChosen = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadedName(file.name);
    setReverseStatus({ kind: 'info', msg: `Extracting legal description from ${file.name}...` });
    try {
      const text = await extractLegalDescriptionFromFile(file);
      setReverseText(text);
      setReverseStatus({ kind: 'success', msg: 'Extracted. Review and click Locate on map.' });
    } catch (err) {
      setReverseStatus({ kind: 'error', msg: err.message || 'Extraction failed.' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, []);

  return (
    <div className="app">
      <header className="header">
        <img
          src="/brand/aliqon-logo-horizontal-dark.svg"
          alt="ALIQON"
          style={{ height: 42, width: 'auto', display: 'block' }}
        />
        <span className="phase-tag">Legal descriptions, precisely mapped</span>
      </header>
      <aside className="sidebar">
        <SearchBar onSearch={handleSearch} loading={loading} />
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            onClick={() => setBaseLayer((b) => b === 'street' ? 'satellite' : 'street')}
            style={{
              flex: 1, padding: '6px 10px', fontSize: 12,
              background: baseLayer === 'satellite' ? '#111827' : 'white',
              color: baseLayer === 'satellite' ? 'white' : '#111827',
              border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer',
            }}
          >
            {baseLayer === 'satellite' ? 'Satellite view (click for Street)' : 'Street view (click for Satellite)'}
          </button>
        </div>
        <div className="draw-toggle">
          <button className={drawMode ? 'active' : ''} onClick={() => setDrawMode((m) => !m)}>
            {drawMode ? 'Cancel drawing' : 'Draw a polygon'}
          </button>
          {drawMode && (
            <button
              disabled={vertexCount < 3}
              onClick={() => setFinishSignal((n) => n + 1)}
              title={vertexCount < 3 ? 'Click at least 3 points' : 'Finish polygon'}
            >
              Finish ({vertexCount})
            </button>
          )}
        </div>
        {drawMode && (
          <div className="status info">Click on the map to add points. When you have 3+, click "Finish".</div>
        )}
        {status && <div className={`status ${status.kind}`}>{status.msg}</div>}

        <div className="reverse-section">
          <h3 style={{ marginBottom: 8, fontSize: 13, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Locate by description
          </h3>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/gif,image/webp"
            onChange={handleFileChosen}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              width: '100%', padding: '8px 10px', marginBottom: 6, background: 'white', color: '#111827',
              border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer',
            }}
          >
            {uploading ? 'Extracting with Claude...' : (uploadedName ? `\u{1F4C4} ${uploadedName} (pick another)` : 'Upload PDF or image')}
          </button>
          <textarea
            value={reverseText}
            onChange={(e) => setReverseText(e.target.value)}
            placeholder={'Or paste a legal description here. Example:\n"The NE 1/4 of Section 18, Township 6 North, Range 52 West, 6th Principal Meridian, Colorado"'}
            rows={5}
            style={{
              width: '100%', padding: 8, fontSize: 13, fontFamily: 'inherit',
              border: '1px solid #d1d5db', borderRadius: 6, resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button
              type="button"
              onClick={handleReverseLookup}
              disabled={reverseLoading || !reverseText.trim()}
              style={{
                flex: 1, padding: '8px 10px', background: 'var(--aliqon-green)', color: 'white',
                border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              }}
            >
              {reverseLoading ? 'Locating...' : 'Locate on map'}
            </button>
            {(reverseGeom || reverseText || uploadedName) && (
              <button
                type="button"
                onClick={clearReverse}
                style={{
                  padding: '8px 10px', background: 'white', color: '#111827',
                  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                }}
              >
                Clear
              </button>
            )}
          </div>
          {reverseStatus && (
            <div className={`status ${reverseStatus.kind}`} style={{ marginTop: 6 }}>
              {reverseStatus.msg}
            </div>
          )}
        </div>

        <ParcelPanel parcels={parcels} />
      </aside>
      <main className="map-container">
        <Map
          parcels={parcels}
          reverseGeom={reverseGeom}
          drawMode={drawMode}
          finishSignal={finishSignal}
          onPolygonDrawn={handlePolygonDrawn}
          onVertexCountChange={setVertexCount}
          flyTo={flyTo}
          baseLayer={baseLayer}
        />
      </main>
    </div>
  );
}

// Heuristic: parcel IDs are typically digits/dashes/dots/letters with no
// street words ("St", "Ave", etc.) and no comma (common in addresses).
// Examples: "11-1-10-11-00-000-005.004", "0187S 0800", "38077717100001".
function looksLikeParcelId(s) {
  const t = String(s).trim();
  if (!t) return false;
  if (t.includes(',')) return false;
  if (/\b(st|street|ave|avenue|rd|road|dr|drive|blvd|ln|lane|way|pl|ct|court|hwy|highway|pkwy|parkway|ter|terrace|cir|circle|sq|square)\b/i.test(t)) return false;
  // At least one digit, mostly non-letters, and short enough to be an ID.
  if (!/\d/.test(t)) return false;
  if (t.length > 40) return false;
  const nonAlnum = t.replace(/[A-Za-z0-9]/g, '').length;
  const alnum = t.length - nonAlnum;
  // Accept if at least 60% of chars are digits+dashes+dots+spaces
  return (nonAlnum + (t.match(/\d/g) || []).length) / t.length >= 0.4;
}

function centroid(feature) {
  const g = feature?.geometry;
  if (!g) return [0, 0];
  const coords = g.type === 'Polygon' ? g.coordinates[0]
    : g.type === 'MultiPolygon' ? g.coordinates[0][0] : [];
  if (coords.length === 0) return [0, 0];
  let sx = 0, sy = 0;
  for (const [x, y] of coords) { sx += x; sy += y; }
  return [sx / coords.length, sy / coords.length];
}
